import { readVehicle } from '@theme/tt-vehicle-finder';
import { parseList, verdictFor } from '@theme/tt-fitment-verdict';

/**
 * Fitment check on the cart.
 *
 * The card badge on search is deliberately quiet, because a browsing shopper
 * seeing "different platform" on half a grid is reading information, not an
 * error. The cart is the opposite situation: this is the screen before money
 * changes hands, and a mismatch that survives it becomes a return, a freight
 * bill and a refund. So here a mismatch is stated plainly.
 *
 * Only mismatches speak. Marking every correct line "fits" at checkout is the
 * noise that teaches people to stop reading the one line that matters, and
 * 'unknown' stays silent for the same reason it does on a card — most of this
 * catalogue has fitment filled in, but a kit that has not been filled in yet
 * makes no claim either way.
 *
 * Verdicts are computed client-side because the saved vehicle lives in
 * localStorage; rendering them server-side would let Shopify's full-page
 * cache serve one shopper's truck to the next.
 */

/** @param {{make: string, model: string, year?: number|string}} v */
function vehicleLabel(v) {
  return [v.make, v.model, v.year || ''].filter(Boolean).join(' ').trim();
}

class CartFit extends HTMLElement {
  connectedCallback() {
    const vehicle = readVehicle();
    if (!vehicle) return;

    const verdict = verdictFor(vehicle, {
      makes: parseList(this.dataset.makes),
      models: parseList(this.dataset.models),
      years: parseList(this.dataset.years),
    });

    // Recorded either way so the summary can count without recomputing.
    this.dataset.verdict = verdict;
    if (verdict !== 'no') return;

    const line = document.createElement('div');
    line.className = 'tt-cart__fit';

    const text = document.createElement('span');
    // textContent — the label is built from merchant-entered fitment data.
    text.textContent = (this.dataset.labelNo || '').replace('{{ vehicle }}', vehicleLabel(vehicle));
    line.append(text);

    const href = this.dataset.fitUrl;
    if (href) {
      const link = document.createElement('a');
      link.href = href;
      link.textContent = this.dataset.labelLink || '';
      line.append(link);
    }

    this.replaceChildren(line);
  }
}

class CartFitSummary extends HTMLElement {
  connectedCallback() {
    const vehicle = readVehicle();
    if (!vehicle) return;

    // Rows upgrade before this element in document order, but a microtask
    // costs nothing and removes the dependency on that staying true.
    queueMicrotask(() => {
      const bad = document.querySelectorAll('tt-cart-fit[data-verdict="no"]').length;
      if (!bad) return;

      const template = bad === 1 ? this.dataset.summaryOne : this.dataset.summaryOther;
      const note = document.createElement('p');
      note.className = 'tt-cart__fit-sum';
      note.textContent = (template || '')
        .replace('{{ count }}', String(bad))
        .replace('{{ vehicle }}', vehicleLabel(vehicle));
      this.replaceChildren(note);
    });
  }
}

if (!customElements.get('tt-cart-fit')) customElements.define('tt-cart-fit', CartFit);
if (!customElements.get('tt-cart-fit-summary')) customElements.define('tt-cart-fit-summary', CartFitSummary);
